import React, { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import scoreimagesuccesss from "../../assets/images/scoreimagesuccesss.svg";
import scoreimagefailure from "../../assets/images/scoreimagefailure.svg";
import SuccessInfo from "./SuccessInfo";
import { getUserProfile } from "../../Common/data/getUserProfile"; 

interface ScoreCardProps {
  score: string; 
  interviewResult: "pass" | "fail" | "pending" | "available" | "unavailable" | null;
  testTakenStatus: "passed" | "failed" | "pending";
}

const ScoreCard: React.FC<ScoreCardProps> = ({ score, interviewResult, testTakenStatus }) => {
  const [userProfile, setUserProfile] = useState<any>(null);

  // Fetch user profile on component mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profile = await getUserProfile();
        setUserProfile(profile);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchProfile();
  }, []);

  // Determine which image to show based on testTakenStatus
  const imageToShow =
    testTakenStatus === "passed" ? scoreimagesuccesss :
    testTakenStatus === "failed" ? scoreimagefailure : "";

  const remark =
    testTakenStatus === "passed" ? "Congratulations, you have passed the test!" :
    testTakenStatus === "failed" ? "Unfortunately, you did not pass the test. Better luck next time." :
    "Test status is pending.";

  // Determine interview status image and remark (for passed or failed interview)
  const interviewStatus = userProfile?.results.interviewStatus;
  const interviewScore = userProfile?.results.interviewScore;
  
  const interviewImageToShow =
    interviewStatus === "passed" ? scoreimagesuccesss :
    interviewStatus === "failed" ? scoreimagefailure : "";

  const interviewRemark =
    interviewStatus === "passed" ? "Congratulations, you passed the interview!" :
    interviewStatus === "failed" ? "You did not pass the interview. Better luck next time." :
    "";

  return (
    <div>
      {/* Test result card */}
      <Card className="mb-4">
        <div className="position-relative">
          <Card.Body>
            <h6 className="mb-4">Test Score</h6>
            <Row className="text-center align-items-center mb-3">
              <Col xs={6} className="border-end">
                <h2>{score ? score : "N/A"}</h2>
                <p>Test</p>
              </Col>
              <Col xs={6}>
                <h2>{testTakenStatus === "passed" ? "PASS" : testTakenStatus === "failed" ? "FAIL" : "N/A"}</h2>
                <p>Remark</p>
              </Col>
            </Row>
            <Row className="text-center align-items-center">
              <Col>
                {imageToShow && <img src={imageToShow} alt="Score" style={{ maxWidth: "100%", height: "auto" }} />}
              </Col>
            </Row>
          </Card.Body>
        </div>
      </Card>

      {/* Interview result card (only show if passed or failed) */}
      {interviewStatus && (interviewStatus === "passed" || interviewStatus === "failed") && (
        <Card className="mb-4">
          <div className="position-relative">
            <Card.Body>
              <h6 className="mb-4">Interview Score</h6>
              <Row className="text-center align-items-center mb-3">
                <Col xs={6} className="border-end">
                  <h2>{interviewScore ? interviewScore : "N/A"}</h2>
                  <p>Interview</p>
                </Col>
                <Col xs={6}>
                  <h2>{interviewStatus === "passed" ? "PASS" : "FAIL"}</h2>
                  <p>Remark</p>
                </Col>
              </Row>
              <Row className="text-center align-items-center">
                <Col>
                  {interviewImageToShow && (
                    <img src={interviewImageToShow} alt="Interview" style={{ maxWidth: "100%", height: "auto" }} />
                  )}
                </Col>
              </Row>

            </Card.Body>
          </div>
        </Card>
      )}

      {/* Interview meeting details (if scheduled) */}
      {interviewStatus === "scheduled" && (
        <>
          <Card className="mb-0">
            <Card.Body>
              <h6 className="mb-4">Interview</h6>
              <Row className="mb-3">
                <Col>
                  <h4>The Zoom link is now available. Please check the interview details below.</h4>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <Card className="mb-4">
            <Card.Body>
              <h6 className="mb-4">Interview Meeting Details</h6>
              <Row className="mb-3">
                <Col>
                  <div className="d-flex">
                    <p className="mb-1"><strong>Zoom Link: </strong>&nbsp;</p>
                    <a href={userProfile.results.interviewLink} target="_blank" rel="noopener noreferrer">
                      {userProfile.results.interviewLink}
                    </a>
                  </div>
                </Col>
                <Col>
                  <div className="d-flex">
                    <p className="mb-1"><strong>Time: </strong></p>
                    <p>&nbsp;{userProfile.results.interviewTime}</p>
                  </div>
                </Col>
                <Col>
                  <div className="d-flex">
                    <p className="mb-1"><strong>Date: </strong></p>
                    <p>&nbsp;{userProfile.results.interviewDate}</p>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </>
      )}

      {/* Interview meeting details (if rescheduled) */}
      {interviewStatus === "rescheduled" && (
        <>
          <Card className="mb-0">
            <Card.Body>
              <h6 className="mb-4">Interview Rescheduled</h6>
              <Row className="mb-3">
                <Col>
                  <h4>
                    The interview has been <strong>rescheduled</strong>. Please check
                    the updated Zoom link and meeting details below.
                  </h4>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <Card className="mb-4">
            <Card.Body>
              <h6 className="mb-4">Rescheduled Interview Meeting Details</h6>
              <Row className="mb-3">
                <Col>
                  <div className="d-flex">
                    <p className="mb-1">
                      <strong>Updated Zoom Link: </strong>&nbsp;
                    </p>
                    <a
                      href={userProfile.results.interviewLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {userProfile.results.interviewLink}
                    </a>
                  </div>
                </Col>
                <Col>
                  <div className="d-flex">
                    <p className="mb-1">
                      <strong>New Time: </strong>
                    </p>
                    <p>&nbsp;{userProfile.results.interviewTime}</p>
                  </div>
                </Col>
                <Col>
                  <div className="d-flex">
                    <p className="mb-1">
                      <strong>New Date: </strong>
                    </p>
                    <p>&nbsp;{userProfile.results.interviewDate}</p>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  <p className="text-danger">
                    <strong>Note:</strong> This interview was rescheduled. Please
                    make sure to attend the updated meeting.
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </>
      )}

    </div>
  );
};

export default ScoreCard;
